import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";
import { CursorReactiveModule } from "./CursorReactiveModule";

const PI_HALF = Math.PI / 2;

const TAU = Math.PI * 2;
const RAD2DEG = 180 / Math.PI;
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

const shortestArc = (from: number, to: number) => {
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return from + d;
};

const fastAtan2 = (y: number, x: number): number => {
  if (x === 0 && y === 0) return 0;
  const ax = Math.abs(x);
  const ay = Math.abs(y);

  let angle;
  if (ax >= ay) {
    const ratio = ay / (ax === 0 ? 1 : ax);
    angle = ratio;
  } else {
    const ratio = ax / (ay === 0 ? 1 : ay);
    angle = PI_HALF - ratio;
  }

  if (x < 0) {
    angle = Math.PI - angle;
  }
  if (y < 0) {
    angle = -angle;
  }

  return angle;
};

type SpotlightState = {
  angle: number;
  dist: number;
  tAngle: number;
  tDist: number;
  prevDeg: number;
  prevDist: number;
};

const spotState = new WeakMap<StringObject, SpotlightState>();
const getSpot = (o: StringObject): SpotlightState => {
  let s = spotState.get(o);
  if (!s) {
    s = { angle: 0, dist: 0, tAngle: 0, tDist: 0, prevDeg: NaN, prevDist: NaN };
    spotState.set(o, s);
  }
  return s;
};

export class StringSpotlight extends CursorReactiveModule {
  protected onCursorFrame(data: StringData, frame: number): void {
    //throw new Error('Method not implemented.');
  }
  private updateScheduledSpotlight = false;

  constructor(context: any) {
    super(context);
    this.htmlKey = "spotlight";
    this.nearOnly = false;
    this.useAllObjects = true;
    this.maxDistanceMultiplier = 2;
    this.attributesToMap.push(
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (v: number) =>
          clamp01(
            this.tools.adaptiveLerp.process({
              value: v,
              inMin: 0.1,
              inMax: 1.0,
              outMin: 0.05,
              outMax: 0.65,
            })
          ),
      },
      { key: "angle-threshold", type: "number", fallback: 0.2 },
      { key: "distance-threshold", type: "number", fallback: 0.5 },
      { key: "deadzone", type: "number", fallback: 4 },
      { key: "dist-max", type: "number", fallback: 0 }
    );
  }

  initializeObject(
    id: number,
    obj: StringObject,
    el: HTMLElement,
    attrs: Record<string, any>
  ): void {
    super.initializeObject(id, obj, el, attrs);
    obj.setProperty("spotlight-angle-rad", 0);
    obj.setProperty("spotlight-distance", 0);
    obj.setProperty("spotlight-angle-rad-target", 0);
    obj.setProperty("spotlight-distance-target", 0);

    this.ensurePointerState(obj);
    styleTxn.run(() => {
      this.updateSpotlightState(obj, { forceImmediate: true, forceEmit: true, bypassDeadzone: true });
    });
  }

  protected onPointerDelta(obj: StringObject, dx: number, dy: number, dist: number): void {}

  onMouseMoveMeasure(data: StringData) {
    super.onMouseMoveMeasure(data);
    // const targets = this.nearOnly && this.hover.activeObjects().length ? this.hover.activeObjects() : this.objects;
    // targets.forEach((object) => {
    //   const s = getSpot(object);
    // });
  }
  onMutate(data: StringData): void {
    super.onMutate(data);
    const targets = this.getCursorTargets();
    targets.forEach((object) => {
      this.updateSpotlightState(object);
    });
  }

  private ensurePointerState(object: StringObject): void {
    let dx = object.getProperty<number>("dx");
    let dy = object.getProperty<number>("dy");
    let dist = object.getProperty<number>("dist");

    if (
      dx == null ||
      dy == null ||
      dist == null ||
      !isFinite(dx) ||
      !isFinite(dy) ||
      !isFinite(dist)
    ) {
      const { cx, cy } = this.centers.getCenter(object);
      const mx = this.data.cursor.targetX;
      const my = this.data.cursor.targetY;
      dx = mx - cx;
      dy = my - cy;
      dist = Math.sqrt(dx * dx + dy * dy);
      object.setProperty("dx", dx);
      object.setProperty("dy", dy);
      object.setProperty("dist", dist);
    }
  }

  private updateSpotlightState(
    object: StringObject,
    options: { forceImmediate?: boolean; forceEmit?: boolean; bypassDeadzone?: boolean } = {}
  ): void {
    this.ensurePointerState(object);

    const s = getSpot(object);
    const dx: number = object.getProperty("dx") ?? 0;
    const dy: number = object.getProperty("dy") ?? 0;
    const dist: number = object.getProperty("dist") ?? 0;

    const DEADZONE = object.getProperty<number>("deadzone") ?? 4;
    if (options.bypassDeadzone || dist > DEADZONE) {
      s.tAngle = fastAtan2(dy, dx);
    }

    const distMax = object.getProperty<number>("dist-max") ?? 0;
    const normalizedDist = distMax > 0 ? Math.min(dist, distMax) : dist;
    s.tDist = normalizedDist;

    object.setProperty("spotlight-distance-target", s.tDist);
    object.setProperty("spotlight-angle-rad-target", s.tAngle);

    if (options.forceImmediate) {
      s.angle = s.tAngle;
      s.dist = s.tDist;
    } else {
      const lerp = clamp01(object.getProperty<number>("lerp") ?? 0.15);
      const nextA = shortestArc(s.angle, s.tAngle);
      s.angle += (nextA - s.angle) * lerp;
      s.dist += (s.tDist - s.dist) * lerp;
    }

    object.setProperty("spotlight-angle-rad", s.angle);
    object.setProperty("spotlight-distance", s.dist);

    const degRaw = s.angle * RAD2DEG - 90;

    const angleEps = object.getProperty<number>("angle-threshold") ?? 0.2;
    const distEps = object.getProperty<number>("distance-threshold") ?? 0.5;

    const angleChanged =
      options.forceEmit || isNaN(s.prevDeg) || Math.abs(degRaw - s.prevDeg) > angleEps;
    const distChanged =
      options.forceEmit || isNaN(s.prevDist) || Math.abs(s.dist - s.prevDist) > distEps;

    if (angleChanged || distChanged) {
      const cssDeg = Math.round(degRaw * 10) / 10;
      const cssDist = Math.round(s.dist * 10) / 10;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVars(el, {
          "--spotlight-angle": String(cssDeg),
          "--spotlight-angle-deg": `${cssDeg}deg`,
          "--spotlight-distance": String(cssDist),
        });
      });

      s.prevDeg = degRaw;
      s.prevDist = s.dist;

      this.events.emit(`spotlight:update:${object.id}`, { distance: cssDist, angleDeg: cssDeg });
    }
  }

  protected onCursorScrollUpdate(): void {
    const targets = this.getCursorTargets();
    targets.forEach((object) => {
      this.updateSpotlightState(object, { bypassDeadzone: true });
    });
  }
}
